'use client'

import { useCallback } from 'react'

import dynamic from 'next/dynamic'
import { useParams } from 'next/navigation'

import { XIcon } from '@phosphor-icons/react'

import { HeaderLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { useSpaceSubmit } from '@/hooks/space'
import { useRouter } from '@/i18n/navigation'
import { CreationDetail } from '@/lib/api'

import { DesktopCommentsPanel } from '../comments'
import { useWorkContext } from '../work-context'

import { DesktopPlayContainer } from './desktop-play-container'
import { UserSection } from './user-section'

const PromptContainer = dynamic(
  () =>
    import('@/components/business/prompt/prompt-container').then(mod => ({
      default: mod.PromptContainer
    })),
  {
    loading: () => (
      <div
        aria-hidden
        className="min-h-[148px] w-full rounded-3xl border border-light/20 bg-light-800/80"
      />
    )
  }
)

export type DesktopWorkProps = {
  data: CreationDetail
}

export const DesktopWork = ({ data: _data }: DesktopWorkProps) => {
  const { id } = useParams()
  const { setSkipReset } = useWorkContext()
  const router = useRouter()

  // 使用 useSpaceSubmit hook 处理提交逻辑，与 explore 页面保持一致
  // 传递当前 work 页面的 id 作为 replicateJobId
  const {
    handleSubmit: spaceSubmit,
    isProcessing,
    showThinking,
    thinkingText
  } = useSpaceSubmit({
    autoCreateSpace: true,
    replicateJobId: id as string
  })

  // 包装提交函数，在提交前设置 skipReset，防止跳转时重置 store
  const handleSubmit = useCallback(async () => {
    setSkipReset?.(true)
    await spaceSubmit()
  }, [setSkipReset, spaceSubmit])

  const handleClose = useCallback(() => {
    router.back()
  }, [router])

  return (
    <div className="flex size-full flex-col overflow-hidden bg-primary">
      <HeaderLayout />
      <div className="flex size-full pt-header">
        <div className="relative flex h-full flex-1 flex-col gap-10">
          <div className="absolute top-4 left-4 z-30">
            <Button
              className="bg-glass h-10 w-10 rounded-full p-0"
              size="icon"
              variant="ghost"
              onClick={handleClose}
            >
              <XIcon className="text-white" size={24} />
            </Button>
          </div>
          <div className="flex min-h-0 flex-1">
            <div className="flex-shrink-0" />
            <div className="flex h-full flex-1">
              <DesktopPlayContainer />
            </div>
            <div className="flex-shrink-0" />
          </div>
          <div className="pointer-events-auto mx-auto mb-4 w-[850px] max-w-full flex-shrink-0 px-10">
            <PromptContainer
              workDetailMode
              className="w-full"
              isProcessing={isProcessing}
              showThinking={showThinking}
              thinkingText={thinkingText}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
        <div className="m-4 ml-0 flex w-94 flex-col gap-4">
          <UserSection className="flex-shrink-0" />
          <DesktopCommentsPanel className="min-h-0 flex-1" />
        </div>
      </div>
    </div>
  )
}
